import {
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	TextField,
	Box,
	Typography
} from "@mui/material";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import CheckCircleOutlined from "@mui/icons-material/CheckCircleOutlined";
import Autocomplete, {
	createFilterOptions,
} from "@mui/material/Autocomplete";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import WhatsMarked from "react-whatsmarked";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ContactModal from "../ContactModal";

const filter = createFilterOptions({
	trim: true,
});

const NewTicketModal = ({ modalOpen, onClose }) => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const [options, setOptions] = useState([]);
	const [loading, setLoading] = useState(false);
	const [searchParam, setSearchParam] = useState("");
	const [selectedContact, setSelectedContact] = useState(null);
	// Conta no WhatsApp do contato escolhido: null = ainda não verificado.
	// Evita o caso de mandar mensagem para número sem WhatsApp e ela sumir sem
	// aviso nenhum — a plataforma não dizia nada até 26/08/2026.
	const [contatoWhats, setContatoWhats] = useState({ estado: "ocioso", tem: null, motivo: "" });
	const [newContact, setNewContact] = useState({});
	const [contactModalOpen, setContactModalOpen] = useState(false);
	const { user } = useContext(AuthContext);
	const [selectedQueue, setSelectedQueue] = useState("");
	const [selectedWhatsapp, setSelectedWhatsapp] = useState("");
	const [settings, setSettings] = useState([]);

	useEffect(() => {
		const fetchSettings = async () => {
			try {
				const { data } = await api.get("/settings");
				setSettings(data);
			} catch (err) {
				toastError(err);
			}
		};
		fetchSettings();
	}, []);

	const getSettingValue = (key) => {
		const setting = settings.find((s) => s.key === key);
		return setting?.value || null;
	};

	useEffect(() => {
		if (!modalOpen || searchParam.length < 3) {
			setLoading(false);
			return;
		}
		setLoading(true);
		const delayDebounceFn = setTimeout(() => {
			const fetchContacts = async () => {
				try {
					const { data } = await api.get("contacts", {
						params: { searchParam },
					});
					setOptions(data.contacts);
					setLoading(false);
				} catch (err) {
					setLoading(false);
					toastError(err);
				}
			};

			fetchContacts();
		}, 500);
		return () => clearTimeout(delayDebounceFn);
	}, [searchParam, modalOpen]);

	const handleClose = () => {
		onClose();
		setSearchParam("");
		setSelectedContact(null);
		setContatoWhats({ estado: "ocioso", tem: null, motivo: "" });
		setSelectedWhatsapp("");
		setSelectedQueue("");
	};

	// whatsappId vai junto porque o bloqueio do backend é por contato+conexão
	// (só vira global com o setting "openTickets" ligado). Sem ele, o aviso
	// prévio acusava ticket que o backend deixaria criar.
	const checkOpenTickets = useCallback(async (contactId, whatsappId) => {
		try {
			const response = await api.get(
				`/tickets/contact/${contactId}/open`,
				{ params: { whatsappId } }
			);

			if (response?.data?.hasOpenTicket) {
				return { ...response.data.ticket, message: response.data.message };
			}

			return null;
		} catch (err) {
			console.error("Erro ao verificar tickets abertos:", err);
			toastError(err, t);
			return null;
		}
	}, [t]);

	const handleSaveTicket = async (contactId) => {
		if (!contactId || !selectedWhatsapp || !selectedQueue) {
			toast.error("É necessário selecionar um contato, um canal e um setor");
			return;
		}

		setLoading(true);

		try {
			// A checagem roda sempre: mesmo com "openTickets" desativado o backend
			// continua barrando dois atendimentos do mesmo contato na MESMA
			// conexão — avisar antes é melhor que deixar salvar e dar erro.
			const openTicket = await checkOpenTickets(contactId, selectedWhatsapp);

			if (openTicket) {
				const ticketUserId = openTicket.user?.id;

				if (ticketUserId === user?.id) {
					toast.info(
						<WhatsMarked>
							{t("contacts.toasts.redirectTicket")}
						</WhatsMarked>,
						{
							toastId: "redirecting-to-ticket",
						}
					);

					setLoading(false);
					setTimeout(() => {
						navigate(`/tickets/${openTicket.id}`);
						handleClose();
					}, 3000);
					return;
				}

				setLoading(false);
				// Frase pronta do backend (mesma do bloqueio): diz onde está o
				// atendimento, com quem, e o que fazer. Fallback para a chave
				// antiga caso o backend ainda não tenha o campo.
				const assignedUserName = openTicket.user?.name || "Atendente desconhecido";
				const assignedUserChannel = openTicket.whatsapp?.name || "Canal desconhecido";
				const ticketCreatedAt = openTicket.createdAt
					? new Date(openTicket.createdAt).toLocaleDateString("pt-BR")
					: "Data desconhecida";

				toastError({
					message:
						openTicket.message ||
						t("ticketsList.errors.ticketAlreadyOpen", {
							userName: assignedUserName,
							userChannel: assignedUserChannel,
							ticketCreatedAt: ticketCreatedAt,
						}),
				});
				return;
			}

			const { data: ticket } = await api.post("/tickets", {
				contactId: contactId,
				userId: user?.id,
				status: "open",
				queueId: selectedQueue,
				whatsappId: selectedWhatsapp
			});
			
			const isGroup = ticket?.contact?.isGroup;
			const targetTab = isGroup ? "groups" : "open";
			localStorage.setItem("pressticket:changeTab", targetTab);
			
			navigate(`/tickets/${ticket.id}`);
		} catch (err) {
			toastError(err);
		}
		setLoading(false);
		handleClose();
	};

	const verificarWhatsapp = useCallback(async (contato) => {
		if (!contato?.id || contato?.isGroup) {
			setContatoWhats({ estado: "ocioso", tem: null, motivo: "" });
			return;
		}
		setContatoWhats({ estado: "verificando", tem: null, motivo: "" });
		try {
			const { data } = await api.get(`/contacts/${contato.id}/whatsapp`);
			setContatoWhats({
				estado: "pronto",
				tem: data?.hasWhatsapp ?? null,
				motivo: data?.motivo || ""
			});
		} catch (err) {
			// Não atrapalha o fluxo: se a verificação falhar, segue sem opinar.
			setContatoWhats({ estado: "ocioso", tem: null, motivo: "" });
		}
	}, []);

	const handleSelectOption = (e, newValue) => {
		if (newValue?.number) {
			setSelectedContact(newValue);
			verificarWhatsapp(newValue);
		} else if (newValue?.name) {
			setNewContact({ name: newValue.name });
			setContactModalOpen(true);
		}
	};

	const handleCloseContactModal = () => {
		setContactModalOpen(false);
	};

	const handleAddNewContactTicket = (contact) => {
		// Antes chamava handleSaveTicket(contact.id) direto: ao salvar o contato o
		// sistema ja tentava criar o TICKET, mas canal e setor ainda nao tinham sido
		// escolhidos (o modal de contato estava por cima) — caia no guard
		// "e necessario selecionar um contato, um canal e um setor" e o formulario
		// ficava travado com os campos em branco.
		// Agora so seleciona o contato recem-criado e devolve o usuario ao formulario.
		setSelectedContact(contact);
		setOptions(prev =>
			prev.some(o => o.id === contact.id) ? prev : [contact, ...prev]
		);
		setContactModalOpen(false);
	};

	const createAddContactOption = (filterOptions, params) => {
		const filtered = filter(filterOptions, params);

		if (params.inputValue !== "" && !loading && searchParam.length >= 3) {
			filtered.push({
				name: `${params.inputValue}`,
			});
		}

		return filtered;
	};

	const renderOption = (props, option) => {
		if (option.number) {
			return <li {...props}>{`${option.name} - ${option.number}`}</li>;
		} else {
			return <li {...props}>{`${t("newTicketModal.add")} ${option.name}`}</li>;
		}
	};

	const renderOptionLabel = (option) => {
		if (option.number) {
			return `${option.name} - ${option.number}`;
		} else {
			return `${option.name}`;
		}
	};

	return (
		<>
			<ContactModal
				open={contactModalOpen}
				initialValues={newContact}
				onClose={handleCloseContactModal}
				onSave={handleAddNewContactTicket}
			></ContactModal>
			<Dialog open={modalOpen} onClose={handleClose}>
				<DialogTitle id="form-dialog-title">
					{t("newTicketModal.title")}
				</DialogTitle>
				<DialogContent dividers>
					<Grid container spacing={3}>
						<Grid item xs={12}>
							<Autocomplete
								options={options}
								loading={loading}
								fullWidth
								clearOnBlur
								autoHighlight
								freeSolo
								clearOnEscape
								getOptionLabel={renderOptionLabel}
								renderOption={renderOption}
								filterOptions={createAddContactOption}
								onChange={(e, newValue) => handleSelectOption(e, newValue)}
								renderInput={(params) => (
									<TextField
										{...params}
										label={t("newTicketModal.fieldLabel")}
										variant="outlined"
										required
										onChange={(e) => setSearchParam(e.target.value)}
										onKeyPress={(e) => {
											if (loading || !selectedContact) return;
											else if (e.key === "Enter") {
												handleSaveTicket(selectedContact.id);
											}
										}}
										InputProps={{
											...params.InputProps,
											endAdornment: (
												<>
													{loading ? (
														<CircularProgress color="inherit" size={20} />
													) : null}
													{params.InputProps.endAdornment}
												</>
											),
										}}
									/>
								)}
							/>

							{/* Conta no WhatsApp do contato escolhido. Só aparece quando há o
							    que dizer: sem conta (o caso que fazia a mensagem sumir calada)
							    ou quando não deu para verificar. */}
							{selectedContact && contatoWhats.estado === "pronto" && contatoWhats.tem === false && (
								<Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1, p: 1, borderRadius: 1, backgroundColor: "rgba(231,76,60,0.12)" }}>
									<WarningAmberOutlined fontSize="small" sx={{ color: "#C0392B" }} />
									<Typography variant="body2" sx={{ color: "#C0392B" }}>
										Este número <strong>não tem conta no WhatsApp</strong> — a mensagem não vai chegar.
									</Typography>
								</Box>
							)}
							{selectedContact && contatoWhats.estado === "pronto" && contatoWhats.tem === true && (
								<Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
									<CheckCircleOutlined fontSize="small" sx={{ color: "#27AE60" }} />
									<Typography variant="caption" sx={{ color: "#27AE60" }}>
										Número com conta no WhatsApp
									</Typography>
								</Box>
							)}
							{selectedContact && contatoWhats.estado === "pronto" && contatoWhats.tem === null && contatoWhats.motivo && (
								<Typography variant="caption" sx={{ display: "block", mt: 1, opacity: 0.7 }}>
									Não foi possível verificar se o número tem WhatsApp ({contatoWhats.motivo}).
								</Typography>
							)}
							{contatoWhats.estado === "verificando" && (
								<Typography variant="caption" sx={{ display: "block", mt: 1, opacity: 0.7 }}>
									Verificando se o número tem WhatsApp...
								</Typography>
							)}
						</Grid>
						<Grid item xs={12}>
							<FormControl variant="outlined" fullWidth>
								<InputLabel>{t("newTicketModal.select.channel")}</InputLabel>
								<Select
									value={selectedWhatsapp}
									onChange={(e) => setSelectedWhatsapp(e.target.value)}
									label={t("newTicketModal.select.channel")}
								>
									<MenuItem value="">
										{t("newTicketModal.select.none")}
									</MenuItem>
									{Array.isArray(user?.whatsapps) &&
										user?.whatsapps.map((whatsapp) => (
											<MenuItem key={whatsapp.id} value={whatsapp.id}>
												{whatsapp.name}
											</MenuItem>
										))}
								</Select>
							</FormControl>
						</Grid>
						<Grid item xs={12}>
							<FormControl variant="outlined" fullWidth>
								<InputLabel>{t("newTicketModal.select.queue")}</InputLabel>
								<Select
									value={selectedQueue}
									onChange={(e) => setSelectedQueue(e.target.value)}
									label={t("newTicketModal.select.queue")}
								>
									<MenuItem value="">
										{t("newTicketModal.select.none")}
									</MenuItem>
									{user?.queues?.map((queue) => (
										<MenuItem key={queue.id} value={queue.id}>
											{queue.name}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={handleClose}
						color="secondary"
						disabled={loading}
						variant="outlined"
					>
						{t("newTicketModal.buttons.cancel")}
					</Button>
					<ButtonWithSpinner
						variant="contained"
						type="button"
						disabled={!selectedContact || !selectedQueue || !selectedWhatsapp}
						onClick={() => handleSaveTicket(selectedContact.id)}
						color="primary"
						loading={loading}
					>
						{t("newTicketModal.buttons.ok")}
					</ButtonWithSpinner>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default NewTicketModal;
